import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import OpenStreetMap from '../OpenStreetMap/OpenStreetMap';
import s from './LocationPicker.css';

const COLOMBIA_CENTER = { lat: 4.5709, lng: -74.2973 };

class LocationPicker extends React.Component {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
  };

  state = { query: '', suggestions: [], loading: false, message: '' };
  searchTimer = null;

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  requestLocations = async variables => {
    const query = `query($query:String,$lat:Float,$lng:Float,$limit:Int){openStreetMapLocations(query:$query,lat:$lat,lng:$lng,limit:$limit)}`;
    const response = await fetch('/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ query, variables }),
    });
    const result = await response.json();
    return JSON.parse((result.data && result.data.openStreetMapLocations) || '[]');
  };

  handleSearch = event => {
    const query = event.target.value;
    this.setState({ query, message: '' });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (query.trim().length < 3) {
      this.setState({ suggestions: [] });
      return;
    }
    this.searchTimer = setTimeout(async () => {
      this.setState({ loading: true });
      try {
        const suggestions = await this.requestLocations({ query: `${query}, Colombia`, limit: 6 });
        this.setState({ suggestions, loading: false, message: suggestions.length ? '' : 'No encontramos esa dirección. Puedes marcarla directamente en el mapa.' });
      } catch (error) {
        this.setState({ suggestions: [], loading: false, message: 'No fue posible buscar en este momento. Puedes marcar el punto en el mapa.' });
      }
    }, 450);
  };

  applyLocation = location => {
    if (!location) return;
    this.props.onChange({
      country: location.countryName || 'Colombia',
      countryCode: location.country || 'CO',
      state: location.state || '',
      city: location.city || '',
      neighborhood: location.neighborhood || '',
      address: location.street || location.displayName || '',
      street: location.street || location.displayName || '',
      zipcode: location.zipcode || '',
      lat: Number(location.lat),
      lng: Number(location.lng),
      displayName: location.displayName || '',
      isMapTouched: true,
    });
    this.setState({ query: location.displayName || location.street || '', suggestions: [], loading: false, message: 'Ubicación seleccionada correctamente.' });
  };

  selectPoint = async point => {
    this.setState({ loading: true, suggestions: [], message: 'Consultando la dirección del punto...' });
    try {
      const locations = await this.requestLocations({ lat: Number(point.lat), lng: Number(point.lng) });
      this.applyLocation(locations[0] || { ...point, country: 'CO', countryName: 'Colombia' });
    } catch (error) {
      this.applyLocation({ ...point, country: 'CO', countryName: 'Colombia' });
    }
  };

  useCurrentLocation = () => {
    if (!navigator.geolocation) {
      this.setState({ message: 'Este dispositivo no permite obtener la ubicación.' });
      return;
    }
    this.setState({ loading: true, message: 'Obteniendo tu ubicación actual...' });
    navigator.geolocation.getCurrentPosition(
      position => this.selectPoint({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => this.setState({ loading: false, message: 'No pudimos acceder al GPS. Autoriza la ubicación en el navegador o marca el punto en el mapa.' }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  };

  render() {
    const value = this.props.value || {};
    const hasPoint = Number.isFinite(Number(value.lat)) && Number.isFinite(Number(value.lng)) && value.lat !== '' && value.lng !== '';
    const center = hasPoint ? { lat: Number(value.lat), lng: Number(value.lng) } : COLOMBIA_CENTER;
    return <section className={s.picker}>
      <div className={s.heading}>
        <div><h3>Ubica el lugar exacto</h3><p>Busca una dirección, usa el GPS o haz clic y arrastra el pin.</p></div>
        <button type="button" className={s.gpsButton} onClick={this.useCurrentLocation} disabled={this.state.loading}>Usar mi ubicación</button>
      </div>
      <div className={s.searchWrap}>
        <label htmlFor="location-search">Buscar dirección o lugar en Colombia</label>
        <input id="location-search" value={this.state.query} onChange={this.handleSearch} placeholder="Ej. Vereda El Tablazo, Rionegro, Antioquia" autoComplete="off" />
        {this.state.suggestions.length > 0 && <ul className={s.suggestions}>{this.state.suggestions.map((item, index) => <li key={`${item.lat}-${item.lng}-${index}`}><button type="button" onClick={() => this.applyLocation(item)}>{item.displayName}</button></li>)}</ul>}
      </div>
      <div className={s.mapWrap}>
        <OpenStreetMap center={center} zoom={hasPoint ? 16 : 6} minZoom={5} maxZoom={19} height={390} draggableMarker={hasPoint} onMapClick={this.selectPoint} onMarkerDragEnd={this.selectPoint} />
        {!hasPoint && <span className={s.mapHint}>Haz clic sobre el mapa para colocar el pin</span>}
      </div>
      {this.state.message && <p className={s.message} role="status">{this.state.message}</p>}
      {hasPoint && <div className={s.selected}><strong>Dirección seleccionada</strong><span>{value.displayName || [value.address || value.street, value.city, value.state, 'Colombia'].filter(Boolean).join(', ')}</span><small>Coordenadas: {Number(value.lat).toFixed(6)}, {Number(value.lng).toFixed(6)}</small></div>}
      <p className={s.credit}>Mapa y datos de ubicación: OpenStreetMap.</p>
    </section>;
  }
}

export default withStyles(s)(LocationPicker);
